import { ChevronRight } from '@mui/icons-material';
import { IconButton, Stack, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import DashboardCard from '/@/components/bayer/dashboard-card';
import colors from '/@/components/theme-provider/colors';
import settings from '/@/core/settings';
import { useCropMonitoringResults } from '/@/hooks/crop-monitoring/use-crop-monitorings';
import { useProcessRecommendation } from '/@/hooks/my-recommendations/useFilterMyRecommendations';
import { useGetUserNode, useUserNodes } from '/@/hooks/nodes';
import { useIsValidatedConsultant, useIsValidatedRV } from '/@/hooks/session';
import { useVisitReports } from '/@/hooks/visit-reports';
import { _ } from '/@/locale';
import { useGetRecommendationsFromAPI } from '/@/store/private-data/recommendations/apiProviders';
import { formatDate } from '/@/utils';
import { reverse } from '/@/utils/router';

const useRVsLatestActivities = ({ userNodeId }: { userNodeId: number }) => {
    const navigate = useNavigate();
    const isRv = useIsValidatedRV();
    const isConsultant = useIsValidatedConsultant();

    const userNodeQuery = useUserNodes({
        node: userNodeId,
        includeChildren: true,
        type: 'rv',
    });
    const rvUserNodeIds = useMemo(() => {
        return userNodeQuery.data?.results?.map((userNode) => userNode.id) || [];
    }, [userNodeQuery.data]);

    const recommendationsQuery = useGetRecommendationsFromAPI(
        {
            page: 1,
            pageSize: 5,
            filters: {
                created_by__in: rvUserNodeIds,
            },
            ordering: '-created',
            node: userNodeId,
            includeChildren: true,
        },
        {
            revalidateOnMount: true,
        },
        rvUserNodeIds.length > 0
    );
    const visitReportsQuery = useVisitReports(
        {
            page: 1,
            page_size: 5,
            ordering: '-created',
            user_node: userNodeId,
            created_by__in: rvUserNodeIds,
            status: !(isRv || isConsultant) ? 'saved' : undefined,
        },
        {
            enabled: rvUserNodeIds.length > 0,
        }
    );
    const userNodesQuery = useUserNodes(
        {
            page: 1,
            page_size: 5,
            type: 'grower',
            node: userNodeId,
            parent__in: rvUserNodeIds,
        },
        {
            enabled: rvUserNodeIds.length > 0,
        }
    );
    const cropMonitoringResultsQuery = useCropMonitoringResults(
        {
            page: 1,
            pageSize: 5,
            ordering: '-created',
            node: userNodeId,
        },
        {
            enabled: rvUserNodeIds.length > 0,
        }
    );

    const processRecommendation = useProcessRecommendation();

    const latestActivities = useMemo(() => {
        const latestActivities = [];
        if (recommendationsQuery.data) {
            latestActivities.push(
                ...recommendationsQuery.data.results.data.map((recommendation) => {
                    const processedRecommendation = processRecommendation(recommendation);
                    return {
                        type: 'recommendation',
                        id: recommendation.id,
                        created: recommendation.created,
                        creatorUserNodeId: recommendation.createdBy?.id,
                        growerUserNodeId: recommendation.userNode,
                        redirect: () => processedRecommendation.onClick(processedRecommendation),
                        recommendationType: processedRecommendation.iconLabel,
                    };
                })
            );
        }
        if (visitReportsQuery.data) {
            latestActivities.push(
                ...visitReportsQuery.data.results.map((visitReport) => ({
                    type: 'visitReport',
                    id: visitReport.id,
                    created: visitReport.created,
                    creatorUserNodeId: visitReport.createdBy,
                    growerUserNodeId: visitReport.userNode,
                    redirect: () => {
                        navigate(
                            reverse('visitReport.VisitReportDetail', { params: { visitReportId: visitReport.id } })
                        );
                    },
                    recommendationType: undefined,
                }))
            );
        }
        if (userNodesQuery.data) {
            latestActivities.push(
                ...userNodesQuery.data.results.map((userNode) => ({
                    type: 'userNode',
                    id: userNode.id,
                    created: userNode.created,
                    // FIXME: `parent` is deprecated, use `parents?.[0]` instead
                    creatorUserNodeId: userNode.parent,
                    growerUserNodeId: userNode.id,
                    redirect: () => {
                        navigate(reverse('Growers.detail', { params: { growerNodeId: userNode.id } }));
                    },
                    recommendationType: undefined,
                }))
            );
        }
        if (cropMonitoringResultsQuery.data) {
            latestActivities.push(
                ...cropMonitoringResultsQuery.data.results.map((cropMonitoringResult) => ({
                    type: 'cropMonitoringResult',
                    id: cropMonitoringResult.id,
                    created: cropMonitoringResult.created,
                    creatorUserNodeId: cropMonitoringResult.parentUserNode,
                    growerUserNodeId: cropMonitoringResult.userNode,
                    redirect: () => {
                        navigate(
                            reverse('cropMonitoring.CornCropMonitoringDetail.report', {
                                params: { cropMonitoringId: cropMonitoringResult.cropMonitoring },
                            })
                        );
                    },
                    recommendationType: undefined,
                }))
            );
        }
        return latestActivities
            .toSorted((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())
            .slice(0, 5);
    }, [
        recommendationsQuery.data,
        visitReportsQuery.data,
        userNodesQuery.data,
        cropMonitoringResultsQuery.data,
        navigate,
        processRecommendation,
    ]);

    return latestActivities;
};

const LatestActivityItem = ({ activity }: { activity: ReturnType<typeof useRVsLatestActivities>[number] }) => {
    const userNodeQuery = useGetUserNode(activity.creatorUserNodeId || 0);
    const userNode = userNodeQuery.data;

    console.log(userNode);
    console.log(activity.creatorUserNodeId);

    const growerUserNodeQuery = useGetUserNode(activity.growerUserNodeId || 0);
    const growerUserNode = growerUserNodeQuery.data;

    const description = useMemo(() => {
        switch (activity.type) {
            case 'recommendation':
                return _('Generó una recomendación de {{type}} para Productor {{growerName}}', {
                    type: activity?.recommendationType,
                    growerName: growerUserNode?.name,
                });
            case 'visitReport':
                return _('Creó el informe de una visita realizada al Productor {{growerName}}', {
                    growerName: growerUserNode?.name,
                });
            case 'userNode':
                return _('Agregó al Productor {{growerName}} en {{app}}', {
                    growerName: growerUserNode?.name,
                    app: settings.app.name,
                });
            case 'cropMonitoringResult':
                return _('Tiene disponible un nuevo reporte de cosecha del Productor {{growerName}}.', {
                    growerName: growerUserNode?.name,
                });
        }
    }, [activity, growerUserNode?.name]);

    return (
        <Stack
            sx={{
                borderRadius: 1,
                border: (t) => `1px solid ${t.palette.divider}`,
                background: colors.themes[settings.app.theme].mui.primary.infoBackground,
                px: 2,
                py: 1,
            }}
            spacing={2}
        >
            <Stack spacing={0.5}>
                <Stack direction='row' spacing={0.5} alignItems='center' justifyContent='space-between'>
                    <Typography color={'primary'} variant='subtitle1' fontWeight='bold'>
                        {userNode?.name}
                    </Typography>
                    <IconButton size='small' color='secondary' onClick={activity.redirect}>
                        <ChevronRight />
                    </IconButton>
                </Stack>
                <span>
                    <Typography color={'primary.onSurface'} variant='subtitle1' display='inline'>
                        {formatDate(new Date(activity.created))}
                    </Typography>{' '}
                    <Typography color={'primary.mui.inactive'} variant='subtitle1' display='inline'>
                        {description}
                    </Typography>
                </span>
            </Stack>
        </Stack>
    );
};

const RVsLatestActivities = ({ userNodeId }: { userNodeId: number }) => {
    const latestActivities = useRVsLatestActivities({ userNodeId });
    console.log(latestActivities);

    return (
        <DashboardCard title={_('Última actividad')} subtitle={_('Visualizá la actividad más reciente de cada RV')}>
            <Stack height='100%' spacing={1}>
                {latestActivities.map((activity) => (
                    <LatestActivityItem key={activity.id} activity={activity} />
                ))}
            </Stack>
        </DashboardCard>
    );
};

export default RVsLatestActivities;
